import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TeamAnalytics.css';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { personColor, CATEGORICAL } from '../constants/palette';

// Matches the app-wide person-identity palette (App.css --rmc-p-*, mirrored
// in constants/palette.js).
const PERSON_COLORS = {
  'Ginny': personColor('Ginny'),
  'Zac': personColor('Zac'),
  'JQ': personColor('JQ'),
  'Zen': personColor('Zen'),
  'Others': '#8892A0'
};

const API_BASE = '/api';

const COLORS = CATEGORICAL;

const formatRM = (value) => {
  if (value >= 1000000) return `RM ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `RM ${(value / 1000).toFixed(0)}k`;
  return `RM ${value.toFixed(0)}`;
};

const formatFullRM = (value) =>
  `RM ${parseFloat(value).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--rmc-white)', padding: '10px 14px', borderRadius: 8,
      border: '1px solid var(--rmc-line)', boxShadow: '0 6px 20px rgba(59, 91, 219, 0.18)', fontSize: '0.85rem'
    }}>
      <p style={{ fontWeight: 600, marginBottom: 4, color: 'var(--rmc-ink)' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: {formatFullRM(p.value)}
        </p>
      ))}
    </div>
  );
};

function getDateRange(period, month) {
  const [year, mon] = month.split('-').map(Number);
  const lastDay = (y, m) => new Date(y, m, 0).getDate();

  switch (period) {
    case 'monthly':
      return { from: `${month}-01`, to: `${month}-${lastDay(year, mon)}`, label: `${month}` };
    case 'quarterly': {
      const q = Math.ceil(mon / 3);
      const qStart = (q - 1) * 3 + 1;
      const qEnd = q * 3;
      return {
        from: `${year}-${String(qStart).padStart(2,'0')}-01`,
        to: `${year}-${String(qEnd).padStart(2,'0')}-${lastDay(year, qEnd)}`,
        label: `Q${q} ${year}`
      };
    }
    case 'half-yearly': {
      const h = mon <= 6 ? 1 : 2;
      return {
        from: h === 1 ? `${year}-01-01` : `${year}-07-01`,
        to: h === 1 ? `${year}-06-30` : `${year}-12-31`,
        label: `H${h} ${year}`
      };
    }
    case 'yearly':
      return { from: `${year}-01-01`, to: `${year}-12-31`, label: `${year}` };
    case 'custom':
      return null; // handled separately
    default:
      return { from: `${year}-01-01`, to: `${month}-${lastDay(year, mon)}`, label: `YTD ${month}` };
  }
}

function TeamAnalytics({ month, kpiRevenue }) {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [companyRevenue, setCompanyRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState('all');
  const [period, setPeriod] = useState('monthly');
  const [customFrom, setCustomFrom] = useState(`${month.split('-')[0]}-01`);
  const [customTo, setCustomTo] = useState(month);

  const range = period === 'custom'
    ? { from: `${customFrom}-01`, to: `${customTo}-31`, label: `${customFrom} to ${customTo}` }
    : getDateRange(period, month);

  useEffect(() => {
    const SALES_TEAM = ['Ginny', 'Zac', 'JQ', 'Zen'];

    const fetchAll = async () => {
      try {
        setLoading(true);
        const people = await axios.get(`${API_BASE}/dashboard/overview?month=${month}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const allPeople = people.data.salespeople;
        const salesIds = allPeople.filter(p => SALES_TEAM.includes(p.name)).map(p => p.id);
        const otherIds = allPeople.filter(p => !SALES_TEAM.includes(p.name) && parseFloat(p.total_revenue || 0) > 0).map(p => p.id);
        const names = allPeople.reduce((acc, p) => { acc[p.id] = p.name; return acc; }, {});

        const qp = `month=${month}&from=${range.from}&to=${range.to}`;

        // Fetch sales team individually
        const salesResults = await Promise.all(
          salesIds.map(id =>
            axios.get(`${API_BASE}/salesperson/${id}/analytics?${qp}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => ({ id, name: names[id], ...r.data }))
              .catch(() => null)
          )
        );

        // Fetch others and combine
        if (otherIds.length > 0) {
          const otherResults = await Promise.all(
            otherIds.map(id =>
              axios.get(`${API_BASE}/salesperson/${id}/analytics?${qp}`, {
                headers: { Authorization: `Bearer ${token}` }
              }).then(r => r.data)
                .catch(() => null)
            )
          );
          const validOthers = otherResults.filter(Boolean);
          if (validOthers.length > 0) {
            // Merge all others into one entry
            const combined = {
              id: 'others', name: 'Others',
              monthly_stats: {
                current_month_revenue: validOthers.reduce((s, o) => s + o.monthly_stats.current_month_revenue, 0),
                current_month_commission: validOthers.reduce((s, o) => s + o.monthly_stats.current_month_commission, 0),
                jobs_closed: validOthers.reduce((s, o) => s + o.monthly_stats.jobs_closed, 0),
                avg_deal_size: 0,
                ytd_total: validOthers.reduce((s, o) => s + o.monthly_stats.ytd_total, 0)
              },
              revenue_trend: [],
              event_type_breakdown: [],
              venue_type_breakdown: []
            };
            combined.monthly_stats.avg_deal_size = combined.monthly_stats.jobs_closed > 0
              ? combined.monthly_stats.current_month_revenue / combined.monthly_stats.jobs_closed : 0;

            // Merge trends
            const trendMap = {};
            validOthers.forEach(o => o.revenue_trend.forEach(t => {
              if (!trendMap[t.month]) trendMap[t.month] = { month: t.month, revenue: 0, jobs: 0 };
              trendMap[t.month].revenue += t.revenue;
              trendMap[t.month].jobs += t.jobs;
            }));
            combined.revenue_trend = Object.values(trendMap);

            // Merge event types
            const eventMap = {};
            validOthers.forEach(o => o.event_type_breakdown.forEach(e => {
              if (!eventMap[e.event_type]) eventMap[e.event_type] = { event_type: e.event_type, revenue: 0, job_count: 0 };
              eventMap[e.event_type].revenue += e.revenue;
              eventMap[e.event_type].job_count += e.job_count;
            }));
            combined.event_type_breakdown = Object.values(eventMap).sort((a, b) => b.revenue - a.revenue);

            // Merge venues
            const venueMap = {};
            validOthers.forEach(o => o.venue_type_breakdown.forEach(v => {
              if (!venueMap[v.venue_type]) venueMap[v.venue_type] = { venue_type: v.venue_type, revenue: 0, job_count: 0 };
              venueMap[v.venue_type].revenue += v.revenue;
              venueMap[v.venue_type].job_count += v.job_count;
            }));
            combined.venue_type_breakdown = Object.values(venueMap).sort((a, b) => b.revenue - a.revenue);

            salesResults.push(combined);
          }
        }

        setAnalytics(salesResults.filter(Boolean));

        // Fetch company-wide revenue (includes unassigned credit notes)
        const compRes = await axios.get(`${API_BASE}/company/revenue?from=${range.from}&to=${range.to}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanyRevenue(compRes.data);
      } catch (err) {
        console.error('Failed to fetch team analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token, month, range?.from, range?.to]);

  if (loading) return <div className="loading">Loading team analytics...</div>;
  if (!analytics || analytics.length === 0) return null;

  // Totals — use company-wide revenue (includes unassigned credit notes)
  const totalPeriod = companyRevenue ? companyRevenue.total_revenue : analytics.reduce((s, a) => s + (a.monthly_stats.period_total || a.monthly_stats.ytd_total), 0);
  const totalMonthlyComm = analytics.reduce((s, a) => s + a.monthly_stats.current_month_commission, 0);
  const totalJobs = companyRevenue ? companyRevenue.total_jobs : analytics.reduce((s, a) => s + a.monthly_stats.jobs_closed, 0);
  const avgDeal = totalJobs > 0 ? totalPeriod / totalJobs : 0;
  const periodLabel = range?.label || 'YTD';

  // Build comparison data
  const ytdComparison = analytics.map(a => ({
    name: a.name,
    revenue: a.monthly_stats.period_total || a.monthly_stats.ytd_total,
    commission: a.monthly_stats.current_month_commission,
    jobs: a.monthly_stats.jobs_closed,
    avgDeal: a.monthly_stats.avg_deal_size
  })).sort((a, b) => b.revenue - a.revenue);

  // Build combined monthly trend
  const monthMap = {};
  analytics.forEach(a => {
    a.revenue_trend.forEach(t => {
      if (!monthMap[t.month]) monthMap[t.month] = { month: t.month };
      monthMap[t.month][a.name] = t.revenue;
    });
  });
  const combinedTrend = Object.values(monthMap).sort((a, b) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.indexOf(a.month.split(' ')[0]) - months.indexOf(b.month.split(' ')[0]);
  });

  // Combined event type breakdown
  const eventMap = {};
  const filteredAnalytics = selectedPerson === 'all' ? analytics : analytics.filter(a => a.name === selectedPerson);
  filteredAnalytics.forEach(a => {
    a.event_type_breakdown.forEach(e => {
      if (!eventMap[e.event_type]) eventMap[e.event_type] = { event_type: e.event_type, revenue: 0, job_count: 0 };
      eventMap[e.event_type].revenue += e.revenue;
      eventMap[e.event_type].job_count += e.job_count;
    });
  });
  const combinedEvents = Object.values(eventMap).map(e => ({
    ...e, avg_basket: e.job_count > 0 ? e.revenue / e.job_count : 0
  })).sort((a, b) => b.revenue - a.revenue);

  // Combined venue breakdown
  const venueMap = {};
  filteredAnalytics.forEach(a => {
    a.venue_type_breakdown.forEach(v => {
      if (!venueMap[v.venue_type]) venueMap[v.venue_type] = { venue_type: v.venue_type, revenue: 0, job_count: 0 };
      venueMap[v.venue_type].revenue += v.revenue;
      venueMap[v.venue_type].job_count += v.job_count;
    });
  });
  const combinedVenues = Object.values(venueMap).map(v => ({
    ...v, avg_basket: v.job_count > 0 ? v.revenue / v.job_count : 0
  })).sort((a, b) => b.revenue - a.revenue);

  const personNames = analytics.map(a => a.name);

  return (
    <div className="team-analytics">
      {/* Period Selector */}
      <div className="ta-period-bar">
        <div className="ta-period-buttons">
          {[
            { key: 'monthly', label: 'Monthly' },
            { key: 'quarterly', label: 'Quarterly' },
            { key: 'half-yearly', label: 'Half Year' },
            { key: 'yearly', label: 'Yearly' },
            { key: 'ytd', label: 'YTD' },
            { key: 'custom', label: 'Custom Range' },
          ].map(p => (
            <button key={p.key}
              className={`ta-period-btn ${period === p.key ? 'active' : ''}`}
              onClick={() => setPeriod(p.key)}
            >{p.label}</button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="ta-custom-range">
            <label>From:</label>
            <input type="month" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
            <label>To:</label>
            <input type="month" value={customTo} onChange={e => setCustomTo(e.target.value)} />
          </div>
        )}
        <p className="ta-period-label">Showing: <strong>{periodLabel}</strong></p>
      </div>

      {/* Team Totals */}
      <div className="ta-totals">
        <div className="ta-total-card">
          <p className="ta-total-label">{periodLabel} Revenue</p>
          <h3>{formatFullRM(totalPeriod)}</h3>
        </div>
        <div className="ta-total-card">
          <p className="ta-total-label">{periodLabel} Jobs</p>
          <h3>{totalJobs}</h3>
        </div>
        <div className="ta-total-card">
          <p className="ta-total-label">Avg Deal Size</p>
          <h3>{formatFullRM(avgDeal)}</h3>
        </div>
        <div className="ta-total-card">
          <p className="ta-total-label">Monthly Commission ({month})</p>
          <h3>{formatFullRM(totalMonthlyComm)}</h3>
        </div>
      </div>

      {/* Revenue ranking - horizontal bars */}
      <div className="ta-card">
        <h4>Revenue by Salesperson — {periodLabel}</h4>
        <ResponsiveContainer width="100%" height={Math.max(170, ytdComparison.length * 48)}>
          <BarChart data={[...ytdComparison].sort((a, b) => b.revenue - a.revenue)} layout="vertical" margin={{ top: 6, right: 100, left: 6, bottom: 6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--rmc-line-soft)" horizontal={false} />
            <XAxis type="number" tickFormatter={formatRM} tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} />
            <YAxis type="category" dataKey="name" width={72} axisLine={false} tickLine={false} tick={{ fontSize: 13, fill: 'var(--rmc-ink)' }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(22, 27, 34,0.04)' }} />
            <Bar dataKey="revenue" name={`${periodLabel} Revenue`} radius={[0, 8, 8, 0]} barSize={24}>
              {[...ytdComparison].sort((a, b) => b.revenue - a.revenue).map((entry, i) => (
                <Cell key={i} fill={PERSON_COLORS[entry.name] || COLORS[i]} />
              ))}
              <LabelList dataKey="revenue" position="right" formatter={formatFullRM} style={{ fill: 'var(--rmc-ink)', fontWeight: 800, fontSize: 12 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="ta-stats-row">
          {[...ytdComparison].sort((a, b) => b.revenue - a.revenue).map((p, i) => (
            <div className="ta-person-stat" key={i}>
              <span className="ta-dot" style={{ background: PERSON_COLORS[p.name] || COLORS[i] }} />
              <span className="ta-person-name">{p.name}</span>
              <span className="ta-person-jobs">{p.jobs} jobs</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filter + Event/Venue breakdown */}
      <div className="ta-filter-row">
        <h4>Breakdown</h4>
        <div className="ta-filter-buttons">
          <button
            className={`ta-filter-btn ${selectedPerson === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedPerson('all')}
          >All</button>
          {personNames.map(name => (
            <button
              key={name}
              className={`ta-filter-btn ${selectedPerson === name ? 'active' : ''}`}
              onClick={() => setSelectedPerson(name)}
              style={selectedPerson === name ? { background: PERSON_COLORS[name], color: 'white' } : {}}
            >{name}</button>
          ))}
        </div>
      </div>

      <div className="ta-charts-row">
        {/* Event Type */}
        <div className="ta-card">
          <h4>Revenue by Event Type {selectedPerson !== 'all' ? `(${selectedPerson})` : '(All)'}</h4>
          <ResponsiveContainer width="100%" height={Math.max(220, combinedEvents.length * 36)}>
            <BarChart data={combinedEvents} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--rmc-line-soft)" horizontal={false} />
              <XAxis type="number" tickFormatter={formatRM} tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} />
              <YAxis type="category" dataKey="event_type" tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} width={120} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} barSize={20}>
                {combinedEvents.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="ta-mini-table">
            <div className="ta-mini-row ta-mini-header-row">
              <span className="ta-dot" style={{ opacity: 0 }} />
              <span className="ta-mini-label"><strong>Event Type</strong></span>
              <span className="ta-mini-count"><strong>Jobs</strong></span>
              <span className="ta-mini-avg"><strong>Avg Basket</strong></span>
              <span className="ta-mini-value"><strong>Revenue</strong></span>
            </div>
            {combinedEvents.map((e, i) => (
              <div className="ta-mini-row" key={i}>
                <span className="ta-dot" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="ta-mini-label">{e.event_type}</span>
                <span className="ta-mini-count">{e.job_count}</span>
                <span className="ta-mini-avg">{formatFullRM(e.avg_basket)}</span>
                <span className="ta-mini-value">{formatFullRM(e.revenue)}</span>
              </div>
            ))}
            <div className="ta-mini-row ta-mini-total-row">
              <span className="ta-dot" style={{ opacity: 0 }} />
              <span className="ta-mini-label"><strong>Total</strong></span>
              <span className="ta-mini-count"><strong>{combinedEvents.reduce((s, e) => s + e.job_count, 0)}</strong></span>
              <span className="ta-mini-avg"><strong>{formatFullRM(combinedEvents.reduce((s, e) => s + e.revenue, 0) / (combinedEvents.reduce((s, e) => s + e.job_count, 0) || 1))}</strong></span>
              <span className="ta-mini-value"><strong>{formatFullRM(combinedEvents.reduce((s, e) => s + e.revenue, 0))}</strong></span>
            </div>
          </div>
        </div>

        {/* Venue */}
        <div className="ta-card">
          <h4>Revenue by Venue {selectedPerson !== 'all' ? `(${selectedPerson})` : '(All)'}</h4>
          <ResponsiveContainer width="100%" height={Math.max(220, combinedVenues.length * 36)}>
            <BarChart data={combinedVenues} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--rmc-line-soft)" horizontal={false} />
              <XAxis type="number" tickFormatter={formatRM} tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} />
              <YAxis type="category" dataKey="venue_type" tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} width={140} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} barSize={20}>
                {combinedVenues.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="ta-mini-table">
            <div className="ta-mini-row ta-mini-header-row">
              <span className="ta-dot" style={{ opacity: 0 }} />
              <span className="ta-mini-label"><strong>Venue</strong></span>
              <span className="ta-mini-count"><strong>Jobs</strong></span>
              <span className="ta-mini-avg"><strong>Avg Basket</strong></span>
              <span className="ta-mini-value"><strong>Revenue</strong></span>
            </div>
            {combinedVenues.map((v, i) => (
              <div className="ta-mini-row" key={i}>
                <span className="ta-dot" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="ta-mini-label">{v.venue_type}</span>
                <span className="ta-mini-count">{v.job_count}</span>
                <span className="ta-mini-avg">{formatFullRM(v.avg_basket)}</span>
                <span className="ta-mini-value">{formatFullRM(v.revenue)}</span>
              </div>
            ))}
            <div className="ta-mini-row ta-mini-total-row">
              <span className="ta-dot" style={{ opacity: 0 }} />
              <span className="ta-mini-label"><strong>Total</strong></span>
              <span className="ta-mini-count"><strong>{combinedVenues.reduce((s, v) => s + v.job_count, 0)}</strong></span>
              <span className="ta-mini-avg"><strong>{formatFullRM(combinedVenues.reduce((s, v) => s + v.revenue, 0) / (combinedVenues.reduce((s, v) => s + v.job_count, 0) || 1))}</strong></span>
              <span className="ta-mini-value"><strong>{formatFullRM(combinedVenues.reduce((s, v) => s + v.revenue, 0))}</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeamAnalytics;
